import React, { useCallback, useEffect, useState } from "react"
import { IoIosArrowBack } from "react-icons/io"
import { IoSearch } from "react-icons/io5"
import { useLocation, useNavigate, useParams } from "react-router-dom"

import BackgroundWrapper from "@fuse/sharedComponents/backgroundWrapper"
import BlurWrapper from "@fuse/sharedComponents/blurWrapper"
import { blurKeys } from "@fuse/sharedComponents/blurWrapper/blurKeysList"
import InputField from "@fuse/sharedComponents/inputField"
import LoadingSpinner from "@fuse/sharedComponents/loader/LoadingSpinner"
import { showErrorToast } from "@fuse/sharedComponents/themeToast/toastUtils"
import {
  EMessageStatus,
  EVENT_CLASS,
  EventStatusType as GuestStatus,
} from "@letshang/constants"
import { get } from "lodash-es"
import { useRecoilState, useRecoilValue } from "recoil"
import { ROUTES } from "src/App"

import { guestListState } from "@fuse/recoil/events"
import { userState } from "@fuse/recoil/signup"

import { useEventDetails } from "@api/services/events/useEventsApi"

import useFetchEventDetails from "../eventPage/useFetchEventDetails"
import SearchBar from "../explore/component/SearchBar"
import GuestListCore from "./GuestListCore"
import { useGuestListData } from "./useGuestListData"

export type Guest = {
  uid: string
  name: string
  avatarUrl: string
  status: GuestStatus
  age: number
  phoneNumber: string
}

const GuestList = () => {
  const { eventId } = useParams()
  const { state } = useLocation()
  const navigate = useNavigate()
  const user = useRecoilValue(userState)
  const { state: eventDetailsState, refresh: refreshEventDetails } =
    useFetchEventDetails(eventId)
  const currentEventDetails =
    state?.eventDetails || eventDetailsState?.eventDetails

  const guestListRecoil = useRecoilValue(guestListState)

  const {
    isLoading,
    refetchGuestList,
    invitedListData: invitedGuests,
    refetchInvitedListData,
    messageRecords,
    refetchMessageRecords,
    approveApplicantCall,
    disapproveApplicantCall,
    scanTicketCall,
  } = useGuestListData(eventId)

  useEffect(() => {
    if (user && currentEventDetails) {
      if (
        !user?.isLoggedIn ||
        user?.uid !== currentEventDetails?.eventDocData?.initiatorUid
      ) {
        navigate(ROUTES.EVENTS.replace(":eventId", eventId))
      }
    }
  }, [currentEventDetails, user])

  const handleApproveGuest = async (guestDetails: any) => {
    try {
      const res = await approveApplicantCall({
        eventId,
        ticketId: guestDetails.ticketId,
        applicantUid: guestDetails.uid,
      })
      if (res?.success) {
        await refetchGuestList()
        return { success: true }
      } else {
        showErrorToast(
          res?.message || "Something went wrong, please try again later",
        )
        return { success: false }
      }
    } catch (error) {
      console.log(error)
      showErrorToast("Something went wrong, please try again later")
      return { success: false }
    }
  }

  const handleDisapproveGuest = async (guestDetails: any) => {
    try {
      const res = await disapproveApplicantCall({
        eventId,
        ticketId: guestDetails.ticketId,
        applicantUid: guestDetails.uid,
      })
      if (res?.success) {
        await refetchGuestList()
        return { success: true }
      } else {
        showErrorToast(
          res?.message || "Something went wrong, please try again later",
        )
        return { success: false }
      }
    } catch (error) {
      console.log(error)
      showErrorToast("Something went wrong, please try again later")
      return { success: false }
    }
  }

  const handleCheckInGuest = async (guestDetails: any) => {
    try {
      const res = await scanTicketCall({
        eventId,
        ticketData: { uid: guestDetails.uid },
      })
      if (res?.success) {
        await refetchGuestList()
        return { success: true }
      } else {
        showErrorToast(
          res?.message || "Something went wrong, please try again later",
        )
        return { success: false }
      }
    } catch (error) {
      console.log(error)
      showErrorToast("Something went wrong, please try again later")
      return { success: false }
    }
  }

  return (
    <GuestListCore
      eventDetails={currentEventDetails}
      guestlist={guestListRecoil.guestList}
      invitedGuests={invitedGuests}
      messageRecords={messageRecords}
      eventId={eventId}
      isGuestListLoading={isLoading || state?.isLoading}
      handleApproveGuest={handleApproveGuest}
      handleDisapproveGuest={handleDisapproveGuest}
      handleCheckInGuest={handleCheckInGuest}
    />
  )
}

export default GuestList
